import statusConst from '@/consts/status.const.js';
import Vue from 'vue';
import pixAxios from '@/axios/pixAxios.js';
import genericFormService from '@/services/genericConfig/genericForm.utils.js';
import genericTableService from '@/services/genericConfig/genericTable.utils.js';
import pixEvaluateUtils from '@/services/common/pixEvaluate.utils.js';
import appIdParameter from '@/consts/appIdParameterConfig.const.js';

const genericConfigModule = {
  namespaced: true,

  state: {
    genericTabs: {},
    genericTabConfig: {},
    genericTabConfigStatus: {},
    genericFormConfig: {},
    genericTableConfig: {},
  },

  actions: {
    async setGenericTabs({ dispatch, commit }, { data, mode }) {
      try {
        commit('setGenericTabs', { data: data?.sort((a, b) => a.Order - b.Order), mode });
      } catch (error) {
        dispatch('toastModule/error', 'Error loading the property tabs', { root: true });
      }
    },

    async loadGenericTabConfig({ state, dispatch, commit, rootGetters }, containerRowId) {
      if (state.genericTabConfig[containerRowId]) return;
      commit('setGenericTabConfigStatus', { containerRowId, status: statusConst.LOADING });
      try {
          const config = await pixAxios.get('api/GenericConfig/GetContainerConfig', { params: { containerRowId, appId: appIdParameter.PIX } }).then(({ data }) => data && data.sort((a, b) => a.Order - b.Order));
        if (config) {
          const configData = await Promise.all(
            config.reduce((pv, { ComponentId, ComponentType, childs }) => {
              if (ComponentType === 'form' || ComponentType === 'comparableForm') return [...pv, genericFormService.getGenericFormCall(ComponentId).then((formData) => ({ formData }))];
              if (ComponentType === 'table') return [...pv, genericTableService.getGenericTableCall(ComponentId)];
              if (ComponentType === 'tab' && childs?.some(({ ComponentType: ChildComponentType }) => hasGenericConfigCall(ChildComponentType))) {
                return [
                  ...pv,
                  ...childs
                    .filter(({ ComponentType: ChildComponentType }) => hasGenericConfigCall(ChildComponentType))
                      .map(({ ComponentId, ComponentType }) => {
                        if (ComponentType === 'form' || ComponentType === 'comparableForm') return genericFormService.getGenericFormCall(ComponentId).then((formData) => ({ formData }));
                          if (ComponentType === 'table') return genericTableService.getGenericTableCall(ComponentId);
                      }),
                ];
              }
              return pv;
            }, [])
          );
          let i = 0;
          config.forEach((config) => {
            if (hasGenericConfigCall(config.ComponentType)) {
              config.configData = configData[i];
              i = i + 1;
            }
            if (config.ComponentType === 'tab' && config.childs?.some(({ ComponentType: ChildComponentType }) => hasGenericConfigCall(ChildComponentType))) {
              config.childs.forEach((childConfig) => {
                if (hasGenericConfigCall(childConfig.ComponentType)) {
                  childConfig.configData = configData[i];
                  i = i + 1;
                }
              });
            }
          });
          commit('setGenericTabConfig', { containerRowId, config });
        }
        commit('setGenericTabConfigStatus', { containerRowId, status: statusConst.SUCCESS });
      } catch (err) {
        dispatch('toastModule/error', 'Error loading the property tab configuration', { root: true });
        commit('setGenericTabConfigStatus', { containerRowId, status: statusConst.FAIL });
      }
    },

    setGenericFormConfig({ commit }, payload) {
      commit('setGenericFormConfig', payload);
    },

    setGenericTableConfig({ commit }, payload) {
      commit('setGenericTableConfig', payload);
    },
  },

  mutations: {
    setGenericTabs(state, { mode, data }) {
      state.genericTabs[mode] = data;
    },

    setGenericTabConfig(state, { containerRowId, config }) {
      Vue.set(state.genericTabConfig, containerRowId, config);
    },

    setGenericTabConfigStatus(state, { containerRowId, status }) {
      Vue.set(state.genericTabConfigStatus, containerRowId, status);
    },

    setGenericFormConfig(state, { genericFormConfig, formId }) {
      state.genericFormConfig[formId] = genericFormConfig;
    },

    setGenericTableConfig(state, { genericTableConfig, formId }) {
      state.genericTableConfig[formId] = genericTableConfig;
    },
  },

  getters: {
    getGenericTabs:
      (state) =>
      (mode, conditionsData = null) => {
        return state.genericTabs[mode]?.filter(({ Conditions, IsVisible }) => IsVisible && pixEvaluateUtils.evalConditions(Conditions, conditionsData));
      },

    getGenericTabConfig: (state) => (tabId, mode) => {
      const containerRowId = state.genericTabs[mode]?.find(({ IdCategory }) => IdCategory === tabId)?.RowId;
      return state.genericTabConfig[containerRowId];
    },

    getGenericTabConfigStatus: (state) => (tabId, mode) => {
      const containerRowId = state.genericTabs[mode]?.find(({ IdCategory }) => IdCategory === tabId)?.RowId;
      return state.genericTabConfigStatus[containerRowId];
    },

    getTab: (state) => (tabId, mode) => {
      return state.genericTabs[mode]?.find(({ IdCategory }) => IdCategory === tabId);
    },

    getGenericFormConfigById: (state) => (formId) => state.genericFormConfig[formId],

    getGenericTableConfigById: (state) => (tableId) => state.genericTableConfig[tableId],
  },
};

export default genericConfigModule;

const hasGenericConfigCall = (ComponentType) => {
  return ComponentType === 'form' || ComponentType === 'comparableForm' || ComponentType === 'table';
};
